import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ClientsService} from './clients.service';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {ClientConverterService} from './view-models/client-converter.service';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {ClientViewModel} from './view-models/client.view-model';
import {Router} from '@angular/router';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {Title} from '@angular/platform-browser';
import {Keys} from '@shared/keys';
import {finalize} from 'rxjs/operators';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TileComponent} from './tile/tile.component';

@Component({
    selector: 'app-view-clients',
    templateUrl: './view-clients.component.html',
    styleUrls: ['./view-clients.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClientsideDataService],
    imports: [LoadingAnimationComponent, TableTemplateComponent, TileComponent]
})
export class ViewClientsComponent implements OnInit, TableTemplateComponentHost {

    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly clientsService = inject(ClientsService);
    private readonly converter = inject(ClientConverterService);
    private readonly clientsideDataService = inject<ClientsideDataService<ClientViewModel>>(ClientsideDataService);
    private readonly router = inject(Router);
    private readonly titleService = inject(Title);
    private readonly destroyRef = inject(DestroyRef);

    private readonly SEARCH_FIELDS: Keys<ClientViewModel> = ['name'];

    readonly dataLoading = signal(true);
    readonly dataAvailable = signal(false);
    readonly dataEmpty = signal(false);
    readonly columns = signal<TableTemplateColumnConfig[]>([]);
    readonly sortField = signal('name');
    readonly sortOrder = signal(1);
    readonly searchString = signal('');
    readonly pageNumber = signal(1);
    readonly pageSize = signal(20);
    readonly currentPage = signal<PageViewModel<ClientViewModel>>({
        totalElements: 0,
        pageNumber: 1,
        pageSize: 20,
        data: []
    });

    ngOnInit(): void {
        this.dataLoading.set(true);
        this.dataEmpty.set(false);
        this.dataAvailable.set(false);

        this.titleService.setTitle('Portal - Entities');
        this.populateColumns();
        this.loadSavedSort();

        this.clientsService.getEntities()
            .pipe(
                finalize(() => {
                    this.dataLoading.set(false);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: entities => {
                    const clientViewModels = this.converter.convert(entities);
                    this.clientsideDataService.initialize(clientViewModels);
                    this.updateData();
                    this.dataEmpty.set(clientViewModels.length === 0);
                    this.dataAvailable.set(!this.dataEmpty());
                },
                error: error => {
                    console.error('Error loading entities', error);
                    this.dataEmpty.set(true);
                    this.dataAvailable.set(false);
                }
            });
    }

    private loadSavedSort() {
        const storedSort = this.sessionStorageService.getSort(SortedTable.Clients);
        if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
            this.sortField.set(storedSort[0]);
            this.sortOrder.set(storedSort[1]);
        }
    }

    private updateData(): void {
        this.currentPage.set(this.clientsideDataService.generatePage(
            this.sortField(),
            this.sortOrder() === 1,
            this.SEARCH_FIELDS,
            this.searchString(),
            this.pageNumber(),
            this.pageSize()
        ));
    }

    sort(column: TableTemplateColumnConfig): void {
        this.updateSortColumn(column.dataProperty);
        this.pageNumber.set(1);
        this.updateData();
    }

    private updateSortColumn(field: string): void {
        if (this.sortField() === field) {
            this.sortOrder.set(this.sortOrder() * -1);
        } else {
            this.sortField.set(field);
            this.sortOrder.set(1);
        }

        this.sessionStorageService.setSort(SortedTable.Clients, this.sortField(), this.sortOrder());
    }

    search(searchTerm: string): void {
        this.searchString.set(searchTerm);
        this.pageNumber.set(1);
        this.updateData();
    }

    page(targetPage: number): void {
        this.pageNumber.set(targetPage);
        this.updateData();
    }

    rowClicked(dataViewModel: DataViewModel): void {
        const vm = dataViewModel as ClientViewModel;
        this.router.navigate(vm.detailsRouterLinkCommands);
    }

    populateColumns() {
        this.columns.set([
            {
                title: '',
                dataProperty: 'icon',
                cellType: CellType.Icon,
                sortable: false,
                alignment: Alignment.Center,
                width: '4%',
                dataFocusable: false,
                headingFocusable: false
            },
            {
                title: 'Name',
                dataProperty: 'name',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '21%',
                dataFocusable: true,
                headingFocusable: true
            },
            {
                title: 'Type',
                dataProperty: 'type',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '15%',
                dataFocusable: false,
                headingFocusable: true
            },
            {
                title: 'Jurisdiction',
                dataProperty: 'jurisdiction',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '15%',
                dataFocusable: false,
                headingFocusable: true
            },
            {
                title: 'Country of Incorporation',
                dataProperty: 'countryOfIncorporation',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '15%',
                dataFocusable: false,
                headingFocusable: true
            },
            {
                title: 'Administrative Office',
                dataProperty: 'administrativeOffice',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '15%',
                dataFocusable: false,
                headingFocusable: true
            },
            {
                title: 'Registered Office',
                dataProperty: 'registeredOffice',
                cellType: CellType.Text,
                sortable: true,
                alignment: Alignment.Left,
                width: '15%',
                dataFocusable: false,
                headingFocusable: true
            }
        ]);
    }
}
